import type { AxiosInstance } from "axios";
import { stringify } from "query-string";
import type { DataProvider } from "@refinedev/core";
import { axiosInstance, generateSort, generateFilter } from "./utils";
import { logAudit } from "../auditlog-provider/provider";
import * as Sentry from "@sentry/react";

type MethodTypes = "get" | "delete" | "head" | "options";
type MethodTypesWithBody = "post" | "put" | "patch";

export const RestDataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance,
): Required<DataProvider> => ({
  getList: async ({ resource, filters, sorters, meta }) => {
    const url = `${apiUrl}/${resource}`;

    // const { current = 1, pageSize = 10, mode = "server" } = pagination ?? {};

    const { headers: headersFromMeta, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";


    // const queryFilters = generateFilter(filters);

    const query: {
      _start?: number;
      _end?: number;
      _sort?: string;
      _order?: string;
    } = {};

    // if (mode === "server") {
    //   query.start = (current - 1) * pageSize;
    //   query.end = current * pageSize;
    // }

    const generatedSort = generateSort(sorters);
    if (generatedSort) {
      const { _sort, _order } = generatedSort;
      query._sort = _sort.join(",");
      query._order = _order.join(",");
    }

    // const combinedQuery = { ...query, ...queryFilters };


    //MANUALLY CREATING QUERY SPECIFIC TO SUPABASE, TO BE REOMVED ONCE TEZI BACKEND IS READY
    let combinedQuery = query._sort ? { order: `${query._sort}.${query._order}` } : {};
    let queryFilters ={}

    filters?.map((filter) => {
      //@ts-ignore
      queryFilters = {...queryFilters, [filter.field]: `${filter.operator}.${filter.value}`}
    })

    combinedQuery = {...combinedQuery, ...queryFilters} 

    const urlWithQuery = Object.keys(combinedQuery).length
      ? `${url}?${stringify(combinedQuery)}`
      : url;

    // instead of url, urlQuery will be used
    const { data, headers } = await httpClient[requestMethod](urlWithQuery, {
      headers: headersFromMeta,
    });

    const total = +headers["x-total-count"];

    return {
      data,
      total: total || data.length,
    };
  },

  getMany: async ({ resource, ids, meta }) => {
    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";

    const { data } = await httpClient[requestMethod](
      `${apiUrl}/${resource}?${stringify({ id: ids })}`,
      { headers },
    );

    return {
      data: Array.isArray(data) ? data : [data],
    };
  },

  create: async ({ resource, variables, meta }) => {
    const url = `${apiUrl}/${resource}`;
    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "post";

    logAudit(resource, undefined, requestMethod, variables);

    const { data } = await httpClient[requestMethod](url, variables, {
      headers,
    });

    if (Array.isArray(data)) {
      data.forEach((item) => {
        if (item.error) {
          Sentry.captureMessage(item.error);
        }
      });
    }
    return {
      data,
    };
  },

  createMany: async ({ resource, variables, meta }) => {
    const url = `${apiUrl}/${resource}`;
    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "post";

    const { data } = await httpClient[requestMethod](url, variables, {
      headers,
    });

    return {
      data: Array.isArray(data) ? data : [data],
    };
  },

  update: async ({ resource, id, variables, meta }) => {
    const { data: previousData } = await httpClient.get(`${apiUrl}/${resource}/${resource !== 'general-settings' ? id : ''}`, { headers: meta?.headers });
    const url = `${apiUrl}/${resource}/${id}`;
    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "put";
    // const query = { id: `eq.${id}` };
    // const urlWithQuery = `${url}?${stringify(query)}`;

    const { data } = await httpClient[requestMethod](url, variables, {
      headers,
    });

    logAudit(resource, String(id), requestMethod, variables, resource !== 'general-settings' ? previousData : null);

    return {
      data,
    };
  },

  getOne: async ({ resource, id, meta }) => {
    const url = `${apiUrl}/${resource}${id ? `/${id}`: ""}`;
    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypes) ?? "get";
    // const query = { id: `eq.${meta?.id}` };
    // const urlWithQuery = `${url}?${stringify(query)}`;

    const { data } = await httpClient[requestMethod](url, { headers });

    return {
      data: data,
    };
  },

  // getOne: async ({ resource, id, meta }) => {
  //   const url = `${apiUrl}/${resource}/${id}/edit`;
  //   const { headers, method } = meta ?? {};
  //   const requestMethod = (method as MethodTypes) ?? "get";

  //   const query = { id: `eq.${meta?.id}` };
  //   const urlWithQuery = `${url}?${stringify(query)}`;

  //   const { data } = await httpClient[requestMethod](url, { headers });

  //   return {data}
  // },

  deleteOne: async ({ resource, id, variables, meta }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "delete";

    // const query = { id: `eq.${id}` };
    // const urlWithQuery = `${url}?${stringify(query)}`;

    const { data: previousData } = resource !== "orders" ? await httpClient.get(`${apiUrl}/${resource === "accounts/team-members" ? "accounts" : resource}/${id}`, { headers: meta?.headers }) : { data: null };

    logAudit(resource, String(id), requestMethod, variables, previousData);

    const { data } = await httpClient[requestMethod](url, {
      data: variables,
      headers,
    });

    return {
      data,
    };
  },

  updateMany: async ({ resource, ids, variables, meta }) => {
    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "patch";
    const data = ids.map((id) => ({ ...variables, id }));
    const response = await httpClient[requestMethod](`${apiUrl}/${resource}`, data, { headers });

    //@ts-ignore
    logAudit(resource, ids.map(id => String(id)), requestMethod, {status: data[0].status}, undefined, "updateMany");

    return {
      data: response.data,
    };
  },

  deleteMany: async ({ resource, ids, meta }) => {
    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) ?? "delete";

    const response = await Promise.all(
      ids.map((id) =>
        httpClient[requestMethod](`${apiUrl}/${resource}/${id}`, { headers })
      )
    );

    return {
      data: response.map((res) => res.data),
    };
  },

  getApiUrl: () => {
    return apiUrl;
  },

  custom: async ({
    url,
    method,
    filters,
    sorters,
    payload,
    query,
    headers,
  }) => {

    let requestUrl = `${apiUrl}${url}?`;

    if (sorters) {
      const generatedSort = generateSort(sorters);
      if (generatedSort) {
        const { _sort, _order } = generatedSort;
        const sortQuery = {
          _sort: _sort.join(","),
          _order: _order.join(","),
        };
        requestUrl = `${requestUrl}&${stringify(sortQuery)}`;
      }
    }

    if (filters) {
      const filterQuery = generateFilter(filters);
      requestUrl = `${requestUrl}&${stringify(filterQuery)}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${stringify(query)}`;
    }

    let axiosResponse;
    switch (method) {
      case "put":
        axiosResponse = await httpClient.put(requestUrl, payload, {
          headers,
        });
        break;
      case "post":
        axiosResponse = await httpClient.post(requestUrl, payload, {
          headers,
        });
        break;
      case "patch":
        axiosResponse = await httpClient.patch(requestUrl, payload, {
          headers,
        });
        break;
      case "delete":
        axiosResponse = await httpClient.delete(requestUrl, {
          data: payload,
          headers: headers,
        });
        break;
      default:
        axiosResponse = await httpClient.get(requestUrl, {
          headers,
        });
        break;
    }

    const { data } = axiosResponse;

    const urlParts = url.split('/');
    const resource = `${urlParts[urlParts.length - 3]}/${urlParts[urlParts.length - 2]}`;
    const id = urlParts[urlParts.length - 1];

    if (!(id.includes("audit-logs") || id.includes("send-otp") || id.includes("login")) && method !== "get") {
      logAudit(resource, id, method, Array.isArray(payload) ? {data: payload} : payload);
    }

    return Promise.resolve({ data });
  },
});